import React from 'react';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import { dispatchPlaidExit, dispatchPlaidStart } from 'Hooks/useRefreshEvent';
import { INVALID_LINK_TOKEN } from 'Components/UI/PlaidComponent';

function getInitialState() {
  return {
    plaidLinkToken: null,
    isInitialized: false,
    initializationError: null,
    openedSuccessfully: null,
    failedToOpen: null,
  };
}

export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';
export const OPENED_SUCCESSFULLY = 'opened-successfully';
export const FAILED_TO_OPEN = 'failed-to-open';

function reducer(state, action) {
  switch (action.type) {
    case INITIALIZE_SUCCEEDED: {
      const plaidLinkToken = action.plaidLinkCollection ? action.plaidLinkCollection.resources().head().attr('token') : action.linkToken;
      return {
        ...state,
        isInitialized: true,
        plaidLinkToken,
      };
    }
    case OPENED_SUCCESSFULLY: {
      return {
        ...state,
        openedSuccessfully: true,
      };
    }
    case FAILED_TO_OPEN: {
      return {
        ...state,
        failedToOpen: true,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        initializationError: action.initializationError,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export default function usePlaidNewLink(resource, args = {}) {
  const {
    onSuccess,
    onEvent,
    onExit,
    linkToken,
  } = args;

  const [state, dispatch] = React.useReducer(reducer, null, getInitialState);

  const onMount = async () => {
    // if we have a link token use that, no need to fetch the plaid link collection
    if (linkToken) {
      dispatch({
        type: INITIALIZE_SUCCEEDED,
        linkToken,
      });
      return;
    }

    try {
      const plaidLinkCollection = await resource.relationship(fr.relationships.plaidLink.name).link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        plaidLinkCollection,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  const onDismount = () => {};

  useComponentDidMount(onMount, onDismount);

  const handleClickInitiate = React.useCallback(() => {
    if (!state.isInitialized) {
      return null;
    }

    const {
      plaidLinkToken,
    } = state;

    const options = {
      token: plaidLinkToken,
      onEvent: async (eventName, metadata) => {
        switch (eventName) {
          case 'IDENTITY_VERIFICATION_START_STEP':
            dispatch({ type: OPENED_SUCCESSFULLY });
            break;
          default:
            // no-op
        }
        if (typeof onEvent === 'function') {
          onEvent(eventName, metadata);
        }
      },
      onExit: async (err, metadata) => {
        if (typeof onExit === 'function') {
          onExit(err, metadata);
        }

        if (!state.openedSuccessfully) {
          dispatch({ type: FAILED_TO_OPEN });
        }

        if (err != null && err.error_code === INVALID_LINK_TOKEN) {
          window.Plaid.destroy();
        }

        dispatchPlaidExit();
      },
      onSuccess: async (publicToken, metadata) => {
        if (typeof onSuccess === 'function') {
          onSuccess(publicToken, metadata);
        }
        dispatchPlaidExit();
      },
    };

    window.Plaid.create(options).open();
    dispatchPlaidStart();
    return null;
  }, [state]);

  return {
    handleClickInitiate,
    isInitialized: state.isInitialized,
    initializationError: state.initializationError,
    failedToOpen: state.failedToOpen,
  };
}
